import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { realtimeUrl } from './api.js';

/**
 * R21 — queue SSE event type → the React Query key its routed view reads. The DB
 * NOTIFY triggers (migrations 0047 + 0058) emit these `type`s; an event invalidates
 * the feed key + `['work-summary']` (the rail badges / Done-today). Keys MUST match
 * the queries in the queue views — pinned by `apps/web/src/lib/realtime.test.ts`.
 */
export const QUEUE_EVENT_KEYS: Record<string, readonly string[]> = {
  'job-changes': ['job-changes'],
  'keep-in-touch': ['keep-in-touch'],
  engagement: ['engagement-feed'],
  invitations: ['invitations'],
  // QA (connect-SSE) — the connect ('People you met') hub now gets ~1s SSE push too
  // (NOTIFY trigger added in 0058); ConnectView reads ['connect-proposals'].
  connect: ['connect-proposals'],
};

/**
 * R9 — the SSE connection state, shared via a tiny external store so the topbar
 * status pill can show a "live" (breathing) dot ONLY while the push stream is
 * actually connected. `useRealtimeEvents` is the single writer (mounted once in
 * AppInner); `useRealtimeConnected` is a read-only subscriber.
 */
let sseConnected = false;
const sseListeners = new Set<() => void>();

function setSseConnected(next: boolean): void {
  if (sseConnected === next) return;
  sseConnected = next;
  for (const l of sseListeners) l();
}

export function useRealtimeConnected(): boolean {
  return useSyncExternalStore(
    (cb) => {
      sseListeners.add(cb);
      return () => sseListeners.delete(cb);
    },
    () => sseConnected,
    () => false,
  );
}

/**
 * batch-176 — real-time push. Subscribe to the server's SSE stream and invalidate
 * the affected React Query keys on each event, so the inbox + open conversation
 * update within ~1s (Gmail-like) instead of waiting for the polling interval.
 * Polling stays as a fallback (LIVE_POLL_MS) in case the stream drops.
 *
 * The server sends PII-free `{type, thread_id}` frames (DB triggers, migration
 * 0036). EventSource auto-reconnects on drop; on each (re)open we do a broad
 * invalidate to catch up on anything missed while disconnected. Only mounted
 * (active) queries actually refetch, so this is cheap.
 */
export function useRealtimeEvents(qc: QueryClient, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    // `['threads']` is a prefix that covers the inbox list AND the Needs-reply
    // summaries / sound clocks (all keyed `['threads', ...]`).
    // Inbox-rebuild Phase 0 — COALESCE a burst of SSE events (a chatty thread can
    // emit several/sec) into at most one inbox invalidate per 300ms, so the list +
    // counts don't refetch-storm (and flicker) on every individual message frame.
    let inboxTimer: ReturnType<typeof setTimeout> | null = null;
    const refreshInbox = () => {
      if (inboxTimer) return; // a refresh is already scheduled within this window
      inboxTimer = setTimeout(() => {
        inboxTimer = null;
        void qc.invalidateQueries({ queryKey: ['threads'] });
      }, 300);
    };

    const es = new EventSource(realtimeUrl(), { withCredentials: true });

    es.onopen = () => {
      setSseConnected(true); // R9 — drives the topbar "live" dot
      refreshInbox();
      void qc.invalidateQueries({ queryKey: ['thread'] }); // the open conversation, if any
    };

    es.onmessage = (ev) => {
      let d: { type?: string; thread_id?: string | null };
      try {
        d = JSON.parse(ev.data) as { type?: string; thread_id?: string | null };
      } catch {
        return; // not a JSON data frame (shouldn't happen; heartbeats are comments)
      }
      // R21 — a queue event refreshes its routed view's feed + the rail counts,
      // NOT the inbox (the queue tables are unrelated to ['threads']).
      const queueKey = d.type ? QUEUE_EVENT_KEYS[d.type] : undefined;
      if (queueKey) {
        void qc.invalidateQueries({ queryKey: queueKey });
        void qc.invalidateQueries({ queryKey: ['work-summary'] });
        return;
      }
      refreshInbox();
      if (d.thread_id) void qc.invalidateQueries({ queryKey: ['thread', d.thread_id] });
    };

    // EventSource reconnects on its own (using the server's retry hint); the
    // polling fallback covers any gap. We only reflect the lost connection so the
    // status pill stops claiming "live" — the next onopen flips it back.
    es.onerror = () => setSseConnected(false);

    return () => {
      es.close();
      if (inboxTimer) clearTimeout(inboxTimer);
      setSseConnected(false);
    };
  }, [qc, enabled]);
}
